import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageContainer } from '@/components/ui/PageContainer/PageContainer';
import { usePageHeader } from '@/hooks/usePageHeader';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card/Card';
import { VStack, HStack } from '@/components/ui/Stack';
import { useNotificationStore } from '@/stores/notificationStore';
import { apiFetch } from '@/lib/api';
import { Loader2, Search, Filter, Check, X, Globe } from 'lucide-react';
import { t } from '@/lib/i18n';

interface TranslationRow {
  id: string;
  namespace: string;
  key: string;
  locale: string;
  value: string;
  version: number;
}

async function fetchTranslations(filters: { namespace?: string; search?: string; locale?: string }): Promise<TranslationRow[]> {
  const params = new URLSearchParams();
  if (filters.namespace) params.set('namespace', filters.namespace);
  if (filters.search) params.set('search', filters.search);
  if (filters.locale) params.set('locale', filters.locale);
  const res = await apiFetch(`/api/translations/admin/all?${params}`);
  const json = await res.json();
  return json.data || [];
}

async function fetchNamespaces(): Promise<string[]> {
  const res = await apiFetch('/api/translations/admin/namespaces');
  const json = await res.json();
  return json.data || [];
}

async function fetchLocales(): Promise<string[]> {
  const res = await apiFetch('/api/translations/admin/locales');
  const json = await res.json();
  return json.data || [];
}

async function updateTranslation(namespace: string, key: string, locale: string, value: string) {
  const res = await apiFetch(`/api/translations/admin/${namespace}/${encodeURIComponent(key)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ locale, value }),
  });
  return res.json();
}

// Group translations by namespace::key → { en: value, de: value, ... }
function groupByKey(rows: TranslationRow[], locales: string[]): Array<{
  namespace: string;
  key: string;
  translations: Record<string, { value: string; id: string }>;
}> {
  const map = new Map<string, { namespace: string; key: string; translations: Record<string, { value: string; id: string }> }>();

  for (const row of rows) {
    const mapKey = `${row.namespace}::${row.key}`;
    if (!map.has(mapKey)) {
      map.set(mapKey, { namespace: row.namespace, key: row.key, translations: {} });
    }
    map.get(mapKey)!.translations[row.locale] = { value: row.value, id: row.id };
  }

  return Array.from(map.values());
}

function EditableCell({ value, onSave }: { value: string; onSave: (v: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  const handleSave = () => {
    if (draft !== value) {
      onSave(draft);
    }
    setEditing(false);
  };

  const handleCancel = () => {
    setDraft(value);
    setEditing(false);
  };

  if (!editing) {
    return (
      <div
        className="px-2 py-1 cursor-pointer hover:bg-accent rounded min-h-[32px] text-sm"
        onClick={() => setEditing(true)}
        title="Click to edit"
      >
        {value || <span className="text-muted-foreground italic">empty</span>}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleSave();
          if (e.key === 'Escape') handleCancel();
        }}
        className="flex-1 px-2 py-1 text-sm border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary-500"
        autoFocus
      />
      <button onClick={handleSave} className="p-1 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30 rounded" title="Save">
        <Check className="w-4 h-4" />
      </button>
      <button onClick={handleCancel} className="p-1 text-muted-foreground hover:bg-accent rounded" title="Cancel">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

export function TranslationsAdmin() {
  usePageHeader({ title: t('Translations'), breadcrumbs: [{ label: t('Admin'), path: '/admin/orgs' }] });

  const queryClient = useQueryClient();
  const { success, error: notifyError } = useNotificationStore();

  const [search, setSearch] = useState('');
  const [nsFilter, setNsFilter] = useState('');
  const [localeFilter, setLocaleFilter] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search
  const searchTimeout = React.useRef<ReturnType<typeof setTimeout>>(undefined);
  const handleSearch = useCallback((val: string) => {
    setSearch(val);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => setDebouncedSearch(val), 300);
  }, []);

  const { data: namespaces = [] } = useQuery({
    queryKey: ['translations-namespaces'],
    queryFn: fetchNamespaces,
  });

  const { data: locales = [] } = useQuery({
    queryKey: ['translations-locales'],
    queryFn: fetchLocales,
  });

  const { data: translations = [], isLoading } = useQuery({
    queryKey: ['translations-admin', nsFilter, debouncedSearch, localeFilter],
    queryFn: () => fetchTranslations({ namespace: nsFilter || undefined, search: debouncedSearch || undefined, locale: localeFilter || undefined }),
  });

  const saveMutation = useMutation({
    mutationFn: (params: { namespace: string; key: string; locale: string; value: string }) =>
      updateTranslation(params.namespace, params.key, params.locale, params.value),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['translations-admin'] });
      success(t('Translation updated'));
    },
    onError: (err: any) => {
      notifyError(err?.message || t('Failed to update translation'));
    },
  });

  const grouped = groupByKey(translations, locales);

  return (
    <PageContainer>
      <VStack spacing={4}>
        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <HStack spacing={4} className="flex-wrap">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search keys or values..."
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-border rounded-lg text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-muted-foreground" />
                <select
                  value={nsFilter}
                  onChange={(e) => setNsFilter(e.target.value)}
                  className="px-3 py-2 border border-border rounded-lg text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">All namespaces</option>
                  {namespaces.map((ns) => (
                    <option key={ns} value={ns}>{ns}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-muted-foreground" />
                <select
                  value={localeFilter}
                  onChange={(e) => setLocaleFilter(e.target.value)}
                  className="px-3 py-2 border border-border rounded-lg text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">All languages</option>
                  {locales.map((loc) => (
                    <option key={loc} value={loc}>{loc.toUpperCase()}</option>
                  ))}
                </select>
              </div>
              <div className="text-sm text-muted-foreground">
                {grouped.length} keys · {locales.length} locales
              </div>
            </HStack>
          </CardContent>
        </Card>

        {/* Translations table */}
        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
              </div>
            ) : grouped.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                {debouncedSearch ? 'No translations match your search.' : 'No translations found. Run the seed script to populate.'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted">
                      <th className="text-left px-4 py-3 font-medium text-muted-foreground w-[100px]">Namespace</th>
                      <th className="text-left px-4 py-3 font-medium text-muted-foreground w-[250px]">Key</th>
                      {locales.map((locale) => (
                        <th key={locale} className="text-left px-4 py-3 font-medium text-muted-foreground min-w-[200px]">
                          {locale.toUpperCase()}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {grouped.map((item) => (
                      <tr key={`${item.namespace}::${item.key}`} className="border-b border-border hover:bg-muted/50">
                        <td className="px-4 py-2">
                          <span className="inline-block px-2 py-0.5 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded text-xs font-medium">
                            {item.namespace}
                          </span>
                        </td>
                        <td className="px-4 py-2 font-mono text-xs text-muted-foreground">
                          {item.key}
                        </td>
                        {locales.map((locale) => (
                          <td key={locale} className="px-4 py-2">
                            <EditableCell
                              value={item.translations[locale]?.value || ''}
                              onSave={(value) =>
                                saveMutation.mutate({
                                  namespace: item.namespace,
                                  key: item.key,
                                  locale,
                                  value,
                                })
                              }
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </VStack>
    </PageContainer>
  );
}
